export function getProjectAnalysisReportHtmlUrl(baseUrl: string, reportId: string): string {
  const trimmed = baseUrl.replace(/\/+$/, '');
  const withoutGraphql = trimmed.endsWith('/graphql')
    ? trimmed.slice(0, -'/graphql'.length)
    : trimmed;
  const base = withoutGraphql.replace(/\/+$/, '');
  return `${base}/project-analysis/reports/${encodeURIComponent(reportId)}/html`;
}

export type ProjectAnalysisHtmlErrorKind = 'unauthorized' | 'forbidden' | 'not_found' | 'unknown';

export type FetchProjectAnalysisReportHtmlResult =
  | { ok: true; html: string }
  | { ok: false; kind: ProjectAnalysisHtmlErrorKind; status?: number };

type FetchOptions = {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
};

function resolveBaseUrl(override?: string): string {
  if (override) return override;
  const fromEnv = import.meta.env.VITE_API_URL as string | undefined;
  return fromEnv || '';
}

export async function fetchProjectAnalysisReportHtml(
  reportId: string,
  idToken: string | null | undefined,
  options?: FetchOptions
): Promise<FetchProjectAnalysisReportHtmlResult> {
  if (!idToken) {
    return { ok: false, kind: 'unauthorized', status: 401 };
  }

  const baseUrl = resolveBaseUrl(options?.baseUrl);
  if (!baseUrl) {
    return { ok: false, kind: 'unknown' };
  }

  const url = getProjectAnalysisReportHtmlUrl(baseUrl, reportId);
  const fetchFn = options?.fetchImpl ?? fetch;

  let response: Response;
  try {
    response = await fetchFn(url, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
  } catch {
    return { ok: false, kind: 'unknown' };
  }

  if (response.status === 200) {
    const html = await response.text();
    return { ok: true, html };
  }

  if (response.status === 401) {
    return { ok: false, kind: 'unauthorized', status: 401 };
  }
  if (response.status === 403) {
    return { ok: false, kind: 'forbidden', status: 403 };
  }
  if (response.status === 404) {
    return { ok: false, kind: 'not_found', status: 404 };
  }

  return { ok: false, kind: 'unknown', status: response.status };
}
